import { useEffect, useState } from "react";
import CloseRounded from "@mui/icons-material/CloseRounded";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { isApiError } from "@bw/shared/api";
import type { PartnerCandidate } from "@bw/shared/types/generated";
import { MaterialPlayerCard } from "../../../common/components/MaterialPlayerCard";
import { usePartnerCandidates } from "../hooks/usePartnerCandidates";
import { useProposePairing } from "../hooks/useProposePairing";

/** Debounce window (ms) before a typed search query re-fires the pool fetch. */
const FINDER_SEARCH_DEBOUNCE_MS = 250;

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

/**
 * PartnerFinderDialog (SLICE 1.4-c) — the information-rich partner finder, opened
 * from a doubles/mixed row's "Find a partner" (TournamentEventList's `finderDraw`).
 * THIN presentation: it renders the backend's linkable candidate pool as
 * PlayerCards and lets the player Propose to one — zero business logic, zero
 * fabricated candidates/numbers.
 *
 *   - ONE search input (name OR pasted uuid), forwarded VERBATIM to the server.
 *     NO ≥2-char gate — an empty query opens showing the whole linkable pool.
 *   - Each PartnerCandidate → a <PlayerCard> (name, avatar, rating tier verbatim,
 *     home facility, relationship, and the short `#abcd` handle — the raw uid is
 *     NEVER shown, there is no copy control) + a Propose button. The candidate's
 *     `entry_id` rides the Propose callback ONLY — never the DOM.
 *   - One-proposal latch: the proposing button disables + aria-busy while its
 *     mutation is pending, and the click early-returns if a proposal is in
 *     flight, so a double-click fires proposePairing EXACTLY once. On success the
 *     pool refetches (the proposed-to candidate drops out).
 *   - Honest states: a real loading affordance while in flight, the truthful
 *     "No one is looking for a partner yet" empty copy (no invented players/cards),
 *     and an honest error — never a failed search swallowed into an empty pool.
 *
 * WAI-ARIA modal: role=dialog, aria-modal, an accessible name about finding a
 * partner, focus moved in on open, Escape/scrim + a Close button → onClose.
 */
export function PartnerFinderDialog({
  drawId,
  tournamentId,
  drawName,
  onClose,
}: {
  drawId: string;
  /** Needed only so a future inbox view keys off the same tournament (kept for
   *  parity with the register dialog's signature; the finder is draw-scoped). */
  tournamentId: string;
  /** Optional draw name for the accessible label, e.g. "Find a partner — Mixed A". */
  drawName?: string;
  onClose: () => void;
}) {
  void tournamentId; // draw-scoped; the prop documents the calling context.

  const [query, setQuery] = useState("");
  // The entry id of the candidate currently being proposed to — drives the
  // per-card busy/disabled latch so only the clicked card shows "Proposing…".
  const [proposingEntryId, setProposingEntryId] = useState<string | null>(null);

  const debouncedQuery = useDebouncedValue(query, FINDER_SEARCH_DEBOUNCE_MS);
  const candidatesQ = usePartnerCandidates(drawId, debouncedQuery, true);
  const propose = useProposePairing(drawId);

  // Clear the per-card latch once a proposal settles (so the pool can re-enable).
  useEffect(() => {
    if (!propose.isPending) setProposingEntryId(null);
  }, [propose.isPending]);

  function handlePropose(entryId: string) {
    // The latch: while a proposal is in flight a second click is a no-op, so
    // proposePairing fires EXACTLY once.
    if (propose.isPending) return;
    setProposingEntryId(entryId);
    propose.mutate({ partnerEntryId: entryId });
  }

  const candidates = candidatesQ.data ?? [];
  const label = drawName ? `Find a partner — ${drawName}` : "Find a partner";

  return (
    <Dialog
      open
      aria-label={label}
      onClose={() => {
        if (!propose.isPending) onClose();
      }}
      slotProps={{ paper: { sx: { width: "min(100%, 42rem)" } } }}
    >
      <DialogTitle component="div">
        <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
          <Typography component="h2" variant="h6" sx={{ flex: 1 }}>
            {label}
          </Typography>
          {/* Compact icon Close (top-right). The visible glyph is decorative; the
              accessible name stays "Close" via aria-label so AT (and the tests)
              read a real label, not "⨉". */}
          <IconButton
            autoFocus
            type="button"
            size="small"
            onClick={onClose}
            disabled={propose.isPending}
            aria-label="Close"
            title="Close"
          >
            <CloseRounded aria-hidden="true" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent>
        {/* ONE search input — name OR a pasted uuid, forwarded verbatim. No
            length gate: an empty query still searches the whole linkable pool. */}
        <Box
          component="form"
          role="search"
          onSubmit={(e) => e.preventDefault()}
          sx={{ mb: 2 }}
        >
          <TextField
            fullWidth
            type="search"
            placeholder="Search by name or paste a player ID"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            slotProps={{
              htmlInput: {
                "aria-label": "Search for a partner by name or player ID",
              },
            }}
          />
        </Box>

        {candidatesQ.isLoading ? (
          <Typography color="text.secondary" role="status" aria-live="polite">
            Finding players…
          </Typography>
        ) : candidatesQ.isError ? (
          <Alert severity="error" role="alert">
            We couldn&apos;t load partners right now. Please try again.
          </Alert>
        ) : candidates.length === 0 ? (
          <Typography color="text.secondary">
            No one is looking for a partner yet.
          </Typography>
        ) : (
          <List disablePadding>
            {candidates.map((c) => {
              const isProposing =
                propose.isPending && proposingEntryId === c.entry_id;
              return (
                <PartnerCandidateRow
                  key={c.entry_id}
                  candidate={c}
                  pending={propose.isPending}
                  isProposing={isProposing}
                  onPropose={() => handlePropose(c.entry_id)}
                />
              );
            })}
          </List>
        )}

        {/* ONE propose-error surface (not per-card). A rejected propose (409 from
            the pairings unique index — the candidate was just taken, or you've
            already proposed) is told to the player HONESTLY in an assertive
            region — never swallowed into a button that silently re-enables, and
            never a fabricated "proposed" confirmation. */}
        {propose.isError ? (
          <Alert severity="error" role="alert" sx={{ mt: 2 }}>
            {mapProposeError(propose.error)}
          </Alert>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function PartnerCandidateRow({
  candidate,
  pending,
  isProposing,
  onPropose,
}: {
  candidate: PartnerCandidate;
  pending: boolean;
  isProposing: boolean;
  onPropose: () => void;
}) {
  return (
    <ListItem divider disableGutters>
      <MaterialPlayerCard
        userId={candidate.user_id}
        displayName={candidate.display_name}
        avatarUrl={candidate.avatar_url}
        rating={candidate.rating}
        homeFacilityName={candidate.home_facility_name}
        relationship={candidate.relationship}
        actions={
          <Button
            type="button"
            size="small"
            variant="contained"
            onClick={onPropose}
            disabled={pending}
            aria-busy={isProposing || undefined}
          >
            {isProposing ? "Proposing…" : "Propose"}
          </Button>
        }
      />
    </ListItem>
  );
}

// ---------------------------------------------------------------------------
// Propose error → honest, actionable player copy, branched on HTTP STATUS
// (mirrors the register dialog's mapEntryError shape).
// ---------------------------------------------------------------------------

function mapProposeError(error: unknown): string {
  if (!isApiError(error)) {
    return "We couldn't send your proposal. Please try again.";
  }
  if (error.status === 409) {
    // The pairings unique index rejected it: the candidate is already in a live
    // pairing, or you already have an outstanding partner request. Worded to
    // avoid any success-sounding phrase ("proposed"/"sent") so it never reads as
    // a confirmation — it is an honest "this didn't go through" message.
    return "They already have a partner, or you already have a request out.";
  }
  return "We couldn't send your partner request. Please try again.";
}
